import { Box } from "@chakra-ui/react";
import { Fragment, useCallback, useEffect, useMemo, useRef } from "react";
import { SearchResultTemplate } from "./Template";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/search/Result").SearchResult[], any>[]} pages
 * @property {boolean} isValidating
 * @property {any} error
 * @property {import("swr/infinite").SWRInfiniteResponse["setSize"]} setSize
 * @property {() => void} onClose
 */
/**
 * @param {Props} props
 */
export function SearchResultItem({
  pages,
  isValidating,
  error,
  setSize,
  onClose,
}) {
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const lastScrollHeightRef = useRef(0);

  const isFullyLoaded = useMemo(() => {
    if (pages) {
      const lastPage = pages[pages.length - 1];
      if (!lastPage) {
        return false;
      }
      // we fallback on page 1 and last_page 1 if the meta is not present
      const isLastestPage =
        (lastPage?.meta?.current_page ?? 1) >= (lastPage?.meta?.last_page ?? 1);

      return isLastestPage;
    }
    return false;
  }, [pages]);

  const loadMoreResults = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (!isValidating && !error && !isFullyLoaded) {
      // if the container has no scrollbar, load more results
      const hasScrollbar = container.scrollHeight > container.clientHeight;

      if (
        !hasScrollbar &&
        container.clientHeight > 0 &&
        container.scrollHeight !== lastScrollHeightRef.current
      ) {
        setSize((size) => size + 1);
      }
      lastScrollHeightRef.current = container.scrollHeight;
    }
  }, [error, isFullyLoaded, isValidating, setSize]);

  // resets the size of the swrInfiniteResponse when the component unmounts
  useEffect(() => {
    return () => {
      setSize(1);
    };
  }, [setSize]);

  const handleScroll = useCallback(
    (event) => {
      const target = event.target;

      // if it is scrolled all the way down
      if (target.scrollHeight - target.scrollTop === target.clientHeight) {
        if (!isFullyLoaded && !isValidating) {
          setSize((size) => size + 1);
        }
      }
    },
    [isFullyLoaded, isValidating, setSize],
  );

  // add mutation observer to container to load more results when the container has no scrollbar
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const observer = new ResizeObserver(loadMoreResults);

    observer.observe(container);

    loadMoreResults();

    return () => {
      observer.disconnect();
    };
  }, [loadMoreResults]);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      maxHeight="calc(48px * 6)"
      overflowY="auto"
      borderRadius="inherit">
      {pages.map((page, indexPage) => {
        return (
          <Fragment key={indexPage}>
            {page.data?.map((searchResult) => {
              return (
                <SearchResultTemplate
                  key={searchResult.id}
                  searchResult={searchResult}
                  onClose={onClose}
                />
              );
            })}
          </Fragment>
        );
      })}
    </Box>
  );
}
